import React, { useContext } from 'react';
import { FormContext } from '../../context/EquipmentFormContext';
import { LuWrench } from 'react-icons/lu';
import { FiCalendar } from 'react-icons/fi';

// ============================================================
// DROPDOWN OPTIONS
// ============================================================

const maintenanceFrequencyOptions = [
  'Weekly',
  'Fortnightly',
  'Monthly',
  'Every 2 Months',
  'Quarterly',
  'Every 6 Months',
  'Annual',
];

const maintenanceResponsibilityOptions = [
  'Internal',
  'Contractor',
  'Manufacturer',
  'Supplier',
  'Internal & Contractor',
];

export default function Step5() {
  const { formData, updateField } = useContext(FormContext);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div className="flex items-start space-x-4 mb-6">

        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <LuWrench className="w-5 h-5 stroke-[2.5]" />
        </div>

        <div>

          <div className="flex items-center space-x-2">

            <span className="text-xs font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              5
            </span>

            <h2 className="text-base font-bold text-indigo-950">
              Maintenance & Servicing
            </h2>

          </div>

          <p className="text-xs text-slate-500 mt-0.5">
            Set up maintenance plan and service provider details.
          </p>

        </div>

      </div>

      {/* ========================================================
          MAINTENANCE PLAN
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* ======================================================
            REQUIRES ROUTINE MAINTENANCE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-2">
            Requires Routine Maintenance?{' '}
            <span className="text-red-500">*</span>
          </label>

          <div className="flex items-center space-x-4 h-9">

            <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

              <input
                type="radio"
                name="routine_maint"
                checked={
                  formData.requiresRoutineMaint === 'Yes'
                }
                onChange={() =>
                  updateField(
                    'requiresRoutineMaint',
                    'Yes'
                  )
                }
                className="w-4 h-4 text-blue-600 border-slate-300"
              />

              <span className="ml-2">
                Yes
              </span>

            </label>

            <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

              <input
                type="radio"
                name="routine_maint"
                checked={
                  formData.requiresRoutineMaint === 'No'
                }
                onChange={() =>
                  updateField(
                    'requiresRoutineMaint',
                    'No'
                  )
                }
                className="w-4 h-4 text-blue-600 border-slate-300"
              />

              <span className="ml-2">
                No
              </span>

            </label>

          </div>

        </div>

        {/* ======================================================
            MAINTENANCE FREQUENCY
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Maintenance Frequency{' '}
            <span className="text-red-500">*</span>
          </label>

          <select
            value={formData.maintenanceFrequency}
            onChange={(e) =>
              updateField(
                'maintenanceFrequency',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            <option value="">
              Select frequency
            </option>

            {maintenanceFrequencyOptions.map(
              (frequency) => (
                <option
                  key={frequency}
                  value={frequency}
                >
                  {frequency}
                </option>
              )
            )}

          </select>

        </div>

        {/* ======================================================
            NEXT MAINTENANCE DATE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Next Maintenance Due Date
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.nextMaintenanceDate}
              onChange={(e) =>
                updateField(
                  'nextMaintenanceDate',
                  e.target.value
                )
              }
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

      </div>

      {/* ========================================================
          SERVICE DETAILS
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">

        {/* ======================================================
            SERVICE PROVIDER
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Service Provider
          </label>

          <input
            type="text"
            value={formData.serviceProvider}
            onChange={(e) =>
              updateField(
                'serviceProvider',
                e.target.value
              )
            }
            placeholder="e.g. ABC Engineering"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

        </div>

        {/* ======================================================
            CONTRACT REFERENCE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Contract Reference{' '}
            <span className="text-slate-400 font-normal">
              (optional)
            </span>
          </label>

          <input
            type="text"
            value={formData.contractReference}
            onChange={(e) =>
              updateField(
                'contractReference',
                e.target.value
              )
            }
            placeholder="e.g. SP-12345"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

        </div>

        {/* ======================================================
            CONTRACT EXPIRY DATE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Contract Expiry Date{' '}
            <span className="text-slate-400 font-normal">
              (optional)
            </span>
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.contractExpiryDate}
              onChange={(e) =>
                updateField(
                  'contractExpiryDate',
                  e.target.value
                )
              }
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

        {/* ======================================================
            MAINTENANCE RESPONSIBILITY
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Maintenance Responsibility
          </label>

          <select
            value={formData.maintenanceResponsibility}
            onChange={(e) =>
              updateField(
                'maintenanceResponsibility',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            <option value="">
              Select responsibility
            </option>

            {maintenanceResponsibilityOptions.map(
              (responsibility) => (
                <option
                  key={responsibility}
                  value={responsibility}
                >
                  {responsibility}
                </option>
              )
            )}

          </select>

        </div>

      </div>

    </div>
  );
}